/**
 * Shared GLTFLoader factory. The runtime-default decimated (-dec) GLBs are
 * meshopt-compressed (extensionsRequired: EXT_meshopt_compression), so a bare
 * `new GLTFLoader()` throws "setMeshoptDecoder must be called before loading
 * compressed files" and every ship/station/weapon mesh falls back to a proxy
 * cone. Constructing loaders through this factory registers the meshopt decoder
 * so the -dec assets parse.
 *
 * KHR_mesh_quantization and EXT_texture_webp are handled natively by three's
 * GLTFLoader in browsers, so no extra wiring is needed for those.
 *
 * KHR_texture_basisu (KTX2/Basis): forward wiring for the 8–16k NASA planet maps
 * and BC7 inscription set described in scripts/asset-pipeline.md. A GLB that
 * references KTX2 textures needs a KTX2Loader whose Basis transcoder support is
 * detected against the live WebGLRenderer (WebGL2 picks BC7/ASTC/ETC per GPU).
 * We attach it ONLY when a renderer is supplied, so the current call sites —
 * which pass no renderer and load no KTX2 assets — behave byte-identically.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

/**
 * Basis transcoder location (basis_transcoder.js + .wasm). Copied from
 * three/examples/jsm/libs/basis/ into public/ by the asset pipeline; only fetched
 * the first time a KTX2 texture actually decodes, so a missing folder is inert
 * until KTX2 assets ship. See scripts/asset-pipeline.md.
 */
export const KTX2_TRANSCODER_PATH = '/vendor/basis/';

/**
 * Build a GLTFLoader with the meshopt decoder registered. `MeshoptDecoder`
 * reports `supported === true` synchronously and its WASM `ready` promise is
 * awaited internally by `decodeGltfBufferAsync`, so the returned loader is safe
 * to use immediately with `loadAsync` — no async readiness gate required here.
 *
 * Pass the live renderer to also enable KHR_texture_basisu (KTX2) support; omit
 * it (the default) for the meshopt-only path the ship/station meshes use today.
 */
export function createGltfLoader(renderer?: THREE.WebGLRenderer): GLTFLoader {
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  if (renderer) loader.setKTX2Loader(createKtx2Loader(renderer));
  return loader;
}

/**
 * KTX2/Basis transcoder for KHR_texture_basisu. `detectSupport` inspects the
 * renderer's WebGL2 capabilities to pick the best on-GPU compressed format; the
 * transcoder WASM is loaded lazily on first decode. Exposed so a caller with a
 * plain (already-KTX2) texture (e.g. a future skybox path) can reuse it.
 */
export function createKtx2Loader(renderer: THREE.WebGLRenderer): KTX2Loader {
  return new KTX2Loader()
    .setTranscoderPath(KTX2_TRANSCODER_PATH)
    .detectSupport(renderer);
}
